import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import type { SketchFace, SketchLooseEdge, SketchVec3 } from './types';
import { planeBasis } from './triangulate';
import { splitEdges, walkLoops, signedArea } from './faceSplit';

/**
 * Loose-edge graph — Sketch's "edges coalesce into faces" rule.
 *
 * Drawn segments in empty space persist as loose edges. Every commit runs
 * detectClosedRegions: candidate planes are the active drawing plane PLUS
 * every plane spanned by the edge set itself, so a loop that leaves the
 * drawing plane (draw up from the ground: the loop is planar on a vertical
 * plane) still closes. On each plane the segments form a planar graph
 * (crossings split, connected components separate), a half-edge face walk
 * enumerates the bounded regions, and every region with positive winding
 * becomes an analytic face — its bounding edges are consumed (they now live
 * as the face's contour, rendered by the solid layer).
 */

const PLANE_EPS = 1e-4;
const P2_EPS = 1e-6;

export interface ClosedRegionResult {
    /** New faces to birth (outer contours + holes, world space). */
    faces: SketchFace[];
    /** Loose edges absorbed into the faces (ids from the input set). */
    consumedIds: string[];
}

interface P2 { x: number; y: number }

/**
 * Detect coplanar closed regions among `edges` lying on one plane and
 * return the faces to birth plus the consumed edge ids. Null when no region
 * closed on this plane.
 */
function regionsOnPlane(
    edges: SketchLooseEdge[],
    plane: THREE.Plane
): ClosedRegionResult | null {
    const n = plane.normal.clone().normalize();
    const { u, v } = planeBasis(n);
    // A reference point on the plane for stable projection.
    const origin = plane.constant === 0
        ? new THREE.Vector3(0, 0, 0)
        : plane.normal.clone().multiplyScalar(-plane.constant);
    const project = (p: SketchVec3): P2 => {
        const d = new THREE.Vector3(...p).sub(origin);
        return { x: d.dot(u), y: d.dot(v) };
    };
    const onPlane = (p: SketchVec3): boolean =>
        Math.abs(new THREE.Vector3(...p).sub(origin).dot(n)) < PLANE_EPS;

    // Segments fully on the plane form the graph; everything else stays loose.
    const planeEdges = edges.filter((e) => onPlane(e.a) && onPlane(e.b));
    if (planeEdges.length < 3) return null;

    const pieces = planeEdges
        .map((e) => ({ a: project(e.a), b: project(e.b), interior: false, src: e.id }))
        .filter((p) => Math.hypot(p.b.x - p.a.x, p.b.y - p.a.y) > P2_EPS);
    const simple = splitEdges(pieces);

    // Connected components of the split graph (crossing pieces share nodes).
    const nodeKey = (p: P2): string => `${Math.round(p.x / P2_EPS)}|${Math.round(p.y / P2_EPS)}`;
    const components: Array<Array<{ a: P2; b: P2; src: string }>> = [];
    const adjacency = new Map<string, number[]>();
    simple.forEach((piece, i) => {
        for (const key of [nodeKey(piece.a), nodeKey(piece.b)]) {
            const list = adjacency.get(key) ?? [];
            list.push(i);
            adjacency.set(key, list);
        }
    });
    const visited = new Set<number>();
    for (let i = 0; i < simple.length; i++) {
        if (visited.has(i)) continue;
        const comp: Array<{ a: P2; b: P2; src: string }> = [];
        const stack = [i];
        visited.add(i);
        while (stack.length > 0) {
            const idx = stack.pop()!;
            comp.push(simple[idx]);
            for (const key of [nodeKey(simple[idx].a), nodeKey(simple[idx].b)]) {
                for (const j of adjacency.get(key) ?? []) {
                    if (!visited.has(j)) {
                        visited.add(j);
                        stack.push(j);
                    }
                }
            }
        }
        components.push(comp);
    }

    const lift = (p: P2): SketchVec3 => [
        origin.x + u.x * p.x + v.x * p.y,
        origin.y + u.y * p.x + v.y * p.y,
        origin.z + u.z * p.x + v.z * p.y,
    ];

    const faces: SketchFace[] = [];
    const consumed = new Set<string>();
    for (const comp of components) {
        const loops = walkLoops(comp);
        const positive = loops.filter((l) => signedArea(l.points) > P2_EPS);
        if (positive.length === 0) continue; // dangling / open component: stays loose

        const newFaces: SketchFace[] = positive.map((loop) => {
            const outer = loop.points.map(lift);
            return {
                id: uuidv4(),
                origin: outer[0],
                normal: [n.x, n.y, n.z] as SketchVec3,
                outer,
                holes: [] as SketchVec3[][],
            };
        });

        // Negative-winding loops of a connected component are its exterior
        // face or the mirror copy of its own boundary — never a hole. A
        // genuine hole boundary is a SEPARATE component (no shared vertices);
        // the spoked ring case already walks out as a single annulus cycle,
        // so no same-component nesting happens here by design.

        faces.push(...newFaces);
        comp.forEach((piece) => consumed.add(piece.src));
    }

    if (faces.length === 0) return null;
    return { faces, consumedIds: Array.from(consumed) };
}

/** Canonical, quantized identity of a geometric plane (sign-independent). */
function planeKey(plane: THREE.Plane): string {
    const n = plane.normal.clone().normalize();
    // Canonical orientation: first non-negligible component positive, so
    // (nx,ny,nz) and its negation hash to the same key.
    let sign = 0;
    if (Math.abs(n.x) > 1e-9) sign = n.x > 0 ? 1 : -1;
    else if (Math.abs(n.y) > 1e-9) sign = n.y > 0 ? 1 : -1;
    else if (Math.abs(n.z) > 1e-9) sign = n.z > 0 ? 1 : -1;
    n.multiplyScalar(sign || 1);
    const q = (v: number): number => Math.round(v / 1e-6);
    return `${q(n.x)}|${q(n.y)}|${q(n.z)}|${q(plane.constant * sign)}`;
}

/**
 * Planes spanned by pairs of loose edges. Two non-parallel edges sharing a
 * corner (or crossing as lines) are coplanar and define that plane, and
 * every bounded planar region contains such a pair — so these candidates
 * cover every plane a closed loop can live on, including loops that leave
 * the active drawing plane (vertical loops drawn up from the ground).
 */
function inferEdgePlanes(edges: SketchLooseEdge[]): THREE.Plane[] {
    const pts = edges.map((e) => ({
        a: new THREE.Vector3(...e.a),
        b: new THREE.Vector3(...e.b),
    }));
    const planes: THREE.Plane[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
            const d1 = pts[i].b.clone().sub(pts[i].a);
            const d2 = pts[j].b.clone().sub(pts[j].a);
            const l1 = d1.length();
            const l2 = d2.length();
            if (l1 < 1e-9 || l2 < 1e-9) continue;
            d1.divideScalar(l1);
            d2.divideScalar(l2);
            const cross = new THREE.Vector3().crossVectors(d1, d2);
            // Parallel/collinear pair: no unique plane.
            if (cross.lengthSq() < 1e-12) continue;
            const n = cross.normalize();
            // Skew lines (perpendicular offset beyond plane epsilon) span
            // no common plane.
            if (Math.abs(pts[j].a.clone().sub(pts[i].a).dot(n)) > PLANE_EPS) continue;
            const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(n, pts[i].a);
            const key = planeKey(plane);
            if (seen.has(key)) continue;
            seen.add(key);
            planes.push(plane);
        }
    }
    return planes;
}

/**
 * Detect coplanar closed regions among `edges` and return the faces to
 * birth plus the consumed edge ids. The active drawing `plane` is tried
 * first, then every plane the edge set itself spans (see inferEdgePlanes);
 * edges consumed by an earlier plane's regions are not reused. Null when no
 * region closed — the caller keeps the edges loose (the dangling-line state).
 */
export function detectClosedRegions(
    edges: SketchLooseEdge[],
    plane: THREE.Plane
): ClosedRegionResult | null {
    const faces: SketchFace[] = [];
    const consumed = new Set<string>();
    const seen = new Set<string>();
    for (const candidate of [plane, ...inferEdgePlanes(edges)]) {
        const key = planeKey(candidate);
        if (seen.has(key)) continue;
        seen.add(key);
        const remaining = consumed.size === 0
            ? edges
            : edges.filter((e) => !consumed.has(e.id));
        if (remaining.length < 3) break;
        const found = regionsOnPlane(remaining, candidate);
        if (found) {
            faces.push(...found.faces);
            found.consumedIds.forEach((id) => consumed.add(id));
        }
    }
    if (faces.length === 0) return null;
    return { faces, consumedIds: Array.from(consumed) };
}
